import React, { useState, useEffect } from 'react';
import { auth, db } from '../../firebase'; 
import { 
    GoogleAuthProvider, 
    signInWithPopup,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    updateProfile,
    signOut,
    onAuthStateChanged
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { useNavigate } from 'react-router-dom'; 
import { motion, AnimatePresence } from 'framer-motion';
import { 
    EnvelopeIcon, 
    LockClosedIcon, 
    UserIcon,
    ExclamationTriangleIcon,
    CodeBracketIcon
} from '@heroicons/react/24/outline';

const Login = () => {
    const navigate = useNavigate(); 
    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(true); 
    const [userData, setUserData] = useState(null);

    const [showWelcome, setShowWelcome] = useState(false);
    const [userName, setUserName] = useState("");
    const [customAlert, setCustomAlert] = useState({ show: false, msg: "", type: "error" });

    // --- 🛡️ مراقب الحالة المطور (إصلاح نظام الترحيب والتحويل السلس) ---
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                try {
                    const userRef = doc(db, "users", user.uid);
                    const userDoc = await getDoc(userRef); 
                    
                    if (userDoc.exists()) {
                        const data = userDoc.data();
                        setUserData(data);
                        
                        // 🔑 المنطق الذهبي: السماح للمدرس أو الطالب المفعل بالدخول
                        const canAccess = data.role === 'teacher' || 
                                         data.isActivated === true || 
                                         data.isSubscribed === true || 
                                         data.active === true;
                        
                        if (canAccess) {
                            setUserName(data.name || user.displayName);
                            setShowWelcome(true);
                            // تأخير التحويل لإعطاء جمالية لشاشة الترحيب السحرية
                            setTimeout(() => {
                                redirectUser(data);
                            }, 2800); 
                        } else {
                            // إيقاف التحميل للطالب غير المفعل لإظهار شاشة القفل
                            setLoading(false);
                        }
                    } else {
                        setUserData(null);
                        setLoading(false);
                    }
                } catch (err) {
                    console.error("Firestore Permission Error (403):", err);
                    setLoading(false);
                }
            } else {
                setUserData(null);
                setLoading(false);
            }
            // تأخير إيقاف التحميل قليلاً لتجنب الرمشة البصرية
            setTimeout(() => setLoading(false), 800);
        });
        return () => unsubscribe();
    }, []);

    const showAlert = (msg, type = "error") => {
        setCustomAlert({ show: true, msg, type });
        setTimeout(() => setCustomAlert({ show: false, msg: "", type: "error" }), 4000);
    };

    // --- 🔔 طلب الإشعارات (يُستدعى عند التفاعل لضمان القبول) ---
    const requestNotificationPermission = async () => {
        if ("Notification" in window && Notification.permission === "default") {
            try {
                await Notification.requestPermission();
            } catch (e) {
                console.warn("إذن الإشعارات تم تجاهله");
            }
        }
    };

    const redirectUser = (uData) => {
        const targetPath = uData.role === 'teacher' ? '/admin' : '/student';
        navigate(targetPath, { replace: true });
    };

    const handleGoogleLogin = async () => {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: 'select_account' });
        
        try {
            setLoading(true);
            await requestNotificationPermission();

            const result = await signInWithPopup(auth, provider);
            const user = result.user;
            const userRef = doc(db, "users", user.uid);
            const userDoc = await getDoc(userRef);
            
            if (!userDoc.exists()) {
                const newData = {
                    name: user.displayName,
                    email: user.email,
                    role: 'student',
                    isActivated: false, 
                    isSubscribed: false,
                    active: false,
                    createdAt: new Date()
                };
                await setDoc(userRef, newData);
                setUserData(newData);
            } else {
                setUserData(userDoc.data());
            }
        } catch (error) {
            if (error.code === 'auth/popup-closed-by-user') {
                showAlert("تم إغلاق النافذة، حاول مجدداً");
            } else {
                showAlert("حدث خطأ في الاتصال، تأكد من الإنترنت");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleEmailAuth = async (e) => {
        e.preventDefault();
        setLoading(true);
        await requestNotificationPermission();
        try {
            if (isLogin) {
                const result = await signInWithEmailAndPassword(auth, email, password);
                const userDoc = await getDoc(doc(db, "users", result.user.uid));
                if (userDoc.exists()) setUserData(userDoc.data());
            } else {
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                await updateProfile(userCredential.user, { displayName: name });
                const newData = {
                    name: name, email: email, role: 'student', isActivated: false, 
                    isSubscribed: false, active: false, createdAt: new Date()
                };
                await setDoc(doc(db, "users", userCredential.user.uid), newData);
                setUserData(newData);
            }
        } catch (error) {
            showAlert("تأكد من البريد الإلكتروني وكلمة المرور");
            setLoading(false);
        }
    };

    const variant = {
        enter: { opacity: 0, x: isLogin ? -20 : 20 },
        center: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: isLogin ? 20 : -20 }
    };

    if (loading && !showWelcome) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <div className="flex flex-col items-center gap-4 text-center">
                    <div className="w-12 h-12 border-4 border-[#007AFF] border-t-transparent rounded-full animate-spin"></div>
                    <p className="font-black text-gray-500 animate-pulse text-sm tracking-widest uppercase font-cairo">Al-Noor Security Check</p>
                </div>
            </div>
        );
    }

    const isForbidden = auth.currentUser && userData && (
        userData.isActivated === false && 
        userData.isSubscribed !== true && 
        userData.active !== true && 
        userData.role !== 'teacher'
    );

    if (isForbidden && !showWelcome) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6 text-center bg-[#F2F2F7]">
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white/80 backdrop-blur-2xl p-10 rounded-[40px] shadow-2xl space-y-6 max-w-md w-full border border-white">
                    <div className="text-7xl">🔑</div>
                    <h2 className="text-2xl font-black text-gray-900 leading-tight font-cairo">المحتوى مقفل</h2>
                    <p className="text-gray-500 font-bold text-sm leading-relaxed font-cairo">عذراً ضلعي، يحتاج حسابك لتفعيل الكود لتتمكن من مشاهدة المحاضرات.</p>
                    <button onClick={() => navigate('/student/activate')} className="w-full py-4 bg-[#007AFF] text-white rounded-2xl font-black shadow-lg shadow-blue-500/30 hover:scale-[1.02] active:scale-98 transition-all font-cairo">تفعيل الكود الآن 🚀</button>
                    <button onClick={() => signOut(auth).then(() => window.location.reload())} className="w-full py-2 text-gray-400 font-bold text-xs hover:underline mt-2 font-cairo">تسجيل الخروج</button>
                </motion.div>
            </div>
        );
    }

    return (
        <div style={{ fontFamily: "'Cairo', sans-serif" }} className="min-h-screen w-full flex items-center justify-center bg-[#F2F2F7] relative overflow-hidden p-4 text-right" dir="rtl">
            
            <AnimatePresence>
                {showWelcome && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[9999] flex items-center justify-center p-4 backdrop-blur-3xl bg-[#007AFF]/10">
                        <motion.div initial={{ scale: 0.5, opacity: 0, y: 100 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.8, opacity: 0 }} className="p-16 rounded-[60px] border border-white/50 shadow-2xl text-center space-y-6 bg-white/90">
                            <div className="text-8xl mb-4 drop-shadow-lg text-blue-500">✨</div>
                            <h2 className="text-4xl font-black text-gray-900 leading-tight font-cairo">أهلاً بك مجدداً!</h2>
                            <p className="text-2xl font-bold text-[#007AFF] tracking-wide font-cairo">{userName || "بطل نـور أكاديمي"}</p>
                            <div className="w-16 h-1.5 border-t-4 border-[#007AFF] border-dotted mx-auto mt-8 animate-pulse"></div>
                            <p className="text-xs text-gray-400 font-bold animate-bounce mt-4 tracking-widest uppercase font-cairo">Preparing Dashboard...</p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {customAlert.show && (
                    <motion.div initial={{ opacity: 0, y: -50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="fixed top-10 z-[10000] bg-white/80 backdrop-blur-xl border border-red-200 px-6 py-4 rounded-[25px] shadow-2xl flex items-center gap-4 text-red-600 font-bold font-cairo">
                        <ExclamationTriangleIcon className="w-6 h-6" />
                        <span>{customAlert.msg}</span>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* الخلفية الملونة */}
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-400/10 blur-[120px] rounded-full" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-400/10 blur-[120px] rounded-full" />

            <motion.div layout className="w-full max-w-[440px] z-10">
                <div className="ios-glass p-10 rounded-[45px] border border-white/50 shadow-2xl backdrop-blur-3xl relative overflow-hidden bg-white/60 text-right">
                    
                    <div className="text-center mb-8">
                        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="mb-4">
                            <img src={process.env.PUBLIC_URL + '/Logo.png'} alt="Logo" className="w-24 h-24 mx-auto object-contain drop-shadow-md" />
                        </motion.div>
                        <h1 className="text-3xl font-black text-gray-800 tracking-tight font-cairo">نور أكاديمي</h1>
                        <p className="text-gray-400 font-bold mt-2 text-sm uppercase tracking-widest font-cairo">Al-Noor Education Platform</p>
                    </div>

                    <form className="space-y-4" onSubmit={handleEmailAuth}>
                        <AnimatePresence mode="wait">
                            <motion.div key={isLogin ? 'login-fields' : 'register-fields'} variants={variant} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }} className="space-y-4">
                                {!isLogin && (
                                    <div className="relative group">
                                        <UserIcon className="w-5 h-5 absolute right-4 top-4 text-gray-400 group-focus-within:text-[#007AFF] transition-colors" />
                                        <input required type="text" placeholder="الاسم الكامل" value={name} onChange={(e)=>setName(e.target.value)} className="w-full bg-white/50 border border-white/20 p-4 pr-12 rounded-[20px] outline-none focus:ring-2 focus:ring-[#007AFF]/20 transition-all font-bold text-gray-700 placeholder:text-gray-400 font-cairo" />
                                    </div>
                                )}

                                <div className="relative group">
                                    <EnvelopeIcon className="w-5 h-5 absolute right-4 top-4 text-gray-400 group-focus-within:text-[#007AFF] transition-colors" />
                                    <input required type="email" placeholder="البريد الإلكتروني" value={email} onChange={(e)=>setEmail(e.target.value)} className="w-full bg-white/50 border border-white/20 p-4 pr-12 rounded-[20px] outline-none focus:ring-2 focus:ring-[#007AFF]/20 transition-all font-bold text-gray-700 placeholder:text-gray-400 text-left font-cairo" />
                                </div>

                                <div className="relative group">
                                    <LockClosedIcon className="w-5 h-5 absolute right-4 top-4 text-gray-400 group-focus-within:text-[#007AFF] transition-colors" />
                                    <input required type="password" placeholder="كلمة المرور" value={password} onChange={(e)=>setPassword(e.target.value)} className="w-full bg-white/50 border border-white/20 p-4 pr-12 rounded-[20px] outline-none focus:ring-2 focus:ring-[#007AFF]/20 transition-all font-bold text-gray-700 placeholder:text-gray-400 text-left font-cairo" />
                                </div>
                            </motion.div>
                        </AnimatePresence>

                        <button disabled={loading} type="submit" className="w-full py-4 bg-[#007AFF] text-white rounded-[22px] font-black shadow-xl shadow-blue-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all mt-4 text-lg flex justify-center items-center font-cairo">
                            {loading ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : (isLogin ? 'تسجيل الدخول' : 'إنشاء حساب جديد')}
                        </button>
                    </form>

                    <div className="relative my-8 flex justify-center items-center">
                        <span className="w-full border-t border-gray-200 absolute"></span>
                        <span className="relative px-4 bg-white/10 backdrop-blur-md text-gray-400 font-bold rounded-full text-xs">OR</span>
                    </div>

                    <button onClick={handleGoogleLogin} type="button" disabled={loading} className="w-full flex items-center justify-center gap-3 bg-white border border-gray-100 text-gray-700 py-4 rounded-[22px] font-black hover:bg-gray-50 transition-all shadow-sm active:scale-95 mb-2 font-cairo">
                        <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="google" className="w-5 h-5" />
                        <span>المتابعة باستخدام Google</span>
                    </button>

                    <div className="text-center mt-10 pt-6 border-t border-gray-100/50">
                        <p className="text-gray-500 text-sm font-bold font-cairo text-center">
                            {isLogin ? (
                                <>ليس لديك حساب؟ <span onClick={() => setIsLogin(false)} className="text-[#007AFF] cursor-pointer mr-2 hover:underline transition-all">سجل الآن</span></>
                            ) : (
                                <>لديك حساب بالفعل؟ <span onClick={() => setIsLogin(true)} className="text-[#007AFF] cursor-pointer mr-2 hover:underline transition-all">تسجيل دخول</span></>
                            )}
                        </p>
                    </div>

                    {/* 🛡️ قسم الحقوق الفاخرة المطور - AL-BARON */}
                    <footer className="mt-12 flex flex-col items-center opacity-40 space-y-4">
                        <div className="flex flex-col items-center gap-1">
                            <div className="flex items-center gap-2">
                                <CodeBracketIcon className="w-4 h-4 text-[#007AFF]" />
                                <span className="text-[9px] font-black tracking-widest uppercase font-cairo">Al-Baron Development</span>
                            </div>
                            <span className="text-[10px] font-black text-[#007AFF] tracking-[0.2em] uppercase font-cairo">MK@AlBARON</span>
                        </div>

                       {/* 🟢 أيقونة الواتساب الملكية للمطور @Albaron86 كختم نهائي بديل */}
<motion.a 
    whileHover={{ scale: 1.1, rotate: 10 }} // غيرت الدوران لليمين ليتماشى مع حركة الواتساب
    whileTap={{ scale: 0.9 }}
    href="https://wa.me/9647739661174" // 👈 ضلعي حط رقمك هنا (بدون صفر البداية)
    target="_blank" 
    rel="noopener noreferrer"
    className="w-10 h-10 rounded-xl bg-[#25D366]/10 text-[#25D366] flex items-center justify-center border border-[#25D366]/20 shadow-sm shadow-[#25D366]/5 transition-all"
    title="تواصل مع المطور عبر واتساب"
>
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.246 2.248 3.484 5.232 3.484 8.412-.003 6.557-5.338 11.892-11.893 11.892-1.997-.001-3.951-.5-5.688-1.448l-6.309 1.656zm6.29-4.143c1.589.944 3.119 1.462 4.827 1.463 5.483 0 9.943-4.461 9.946-9.943.001-2.654-1.034-5.147-2.915-7.028-1.88-1.881-4.37-2.918-7.027-2.919-5.482 0-9.944 4.463-9.947 9.945 0 2.028.613 3.738 1.769 5.339l-1.017 3.714 3.811-1.001zM16.4 12.87c-.282-.141-1.67-.824-1.928-.918-.258-.094-.446-.141-.634.141-.188.282-.728.918-.893 1.105-.165.188-.329.212-.611.071-.282-.141-1.194-.44-2.274-1.402-.841-.75-1.408-1.676-1.573-1.958-.165-.282-.018-.434.123-.574.127-.127.282-.329.424-.494.141-.165.188-.282.282-.471.094-.188.047-.353-.024-.494-.071-.141-.634-1.529-.869-2.094-.23-.553-.465-.477-.634-.486-.163-.009-.353-.01-.541-.01s-.494.071-.753.353c-.259.282-.988.965-.988 2.353 0 1.388 1.012 2.729 1.153 2.918.141.188 1.991 3.041 4.824 4.261.674.29 1.2.463 1.611.592.678.215 1.295.185 1.782.112.543-.081 1.67-.682 1.905-1.341.235-.659.235-1.224.165-1.341-.071-.117-.259-.188-.541-.329z" />
    </svg>
</motion.a>
                    </footer>
                </div>
            </motion.div>
        </div>
    );
};

export default Login;